import { format, formatDistance, parseISO } from "date-fns";
import { fr } from "date-fns/locale";
import { STATUT_SORTIE, NIVEAU_OPTIONS } from "./constants";

// Une sortie déjà en cours, terminée ou annulée ne doit plus pouvoir être
// choisie dans un select (inscription, séance, attribution, incident,
// plongée...) : seules les sorties encore à venir restent sélectionnables.
const STATUTS_SORTIE_NON_SELECTIONNABLES = [
  STATUT_SORTIE.EN_COURS,
  STATUT_SORTIE.TERMINEE,
  STATUT_SORTIE.ANNULEE,
];

export const isSortieSelectionnable = (sortie) =>
  !STATUTS_SORTIE_NON_SELECTIONNABLES.includes(sortie?.statut);

// Même règle que isNiveauCompatible côté backend (activites-service/src/
// utils/roleScope.js NIVEAU_ORDER — ordre identique à NIVEAU_OPTIONS ici) :
// un adhérent peut s'inscrire à une sortie dont le niveau requis est
// inférieur ou égal au sien. Utilisé par InscriptionForm.jsx pour ne
// proposer, dans le select "Sortie", que celles compatibles avec
// l'adhérent concerné (soi-même, ou celui choisi par le président/moniteur)
// — le backend reste l'autorité finale (rejette toute incompatibilité à la
// création), ceci n'est qu'un filtre d'affichage pour éviter de proposer un
// choix qui sera de toute façon refusé.
export const isNiveauCompatible = (adherentNiveau, niveauRequis) => {
  if (!niveauRequis) return true;
  const requisIdx = NIVEAU_OPTIONS.indexOf(niveauRequis);
  if (requisIdx === -1) return true;
  const possedeIdx = NIVEAU_OPTIONS.indexOf(adherentNiveau);
  return possedeIdx >= requisIdx;
};

// Heure de fin estimée = date_heure + duree_estimee (colonne TIME, servie
// en "HH:MM" ou "HH:MM:SS" selon qu'elle vient du formulaire ou de l'API) —
// utilisée pour savoir quand proposer de clôturer une sortie ("À terminer"),
// plus fiable que l'heure de départ seule (une sortie qui vient de démarrer
// ne doit pas être signalée comme "à terminer").
export const getFinEstimeeSortie = (sortie) => {
  if (!sortie?.date_heure) return null;
  const debut = new Date(sortie.date_heure);
  if (Number.isNaN(debut.getTime())) return null;
  const [heures = 0, minutes = 0] = (sortie.duree_estimee || "00:00")
    .split(":")
    .map(Number);
  return new Date(debut.getTime() + (heures * 60 + minutes) * 60 * 1000);
};

// "À terminer" (SortiesPage.jsx, SortieDetails.jsx) : la durée estimée est
// dépassée mais le statut n'a pas été mis à jour manuellement — passage
// "Planifiée" -> "En cours" désormais automatique (SortieService
// .demarrerSortiesEchues, backend), mais "En cours" -> "Terminée" reste une
// décision humaine (l'heure estimée n'est qu'une estimation, pas une
// certitude que la plongée est bien finie).
export const sortieDoitEtreCloturee = (sortie) => {
  const finEstimee = getFinEstimeeSortie(sortie);
  if (!finEstimee) return false;
  return (
    finEstimee < new Date() &&
    [STATUT_SORTIE.PLANIFIEE, STATUT_SORTIE.EN_COURS].includes(sortie?.statut)
  );
};

export const formatRelativeTime = (date) => {
  if (!date) return "-";
  try {
    return formatDistance(
      typeof date === "string" ? parseISO(date) : date,
      new Date(),
      {
        addSuffix: true,
        locale: fr,
      },
    );
  } catch {
    return "-";
  }
};

export const formatDate = (date) => {
  if (!date) return "-";
  try {
    return format(
      typeof date === "string" ? parseISO(date) : date,
      "dd/MM/yyyy",
      { locale: fr },
    );
  } catch {
    return "-";
  }
};

export const formatDateTime = (date) => {
  if (!date) return "-";
  try {
    return format(
      typeof date === "string" ? parseISO(date) : date,
      "dd/MM/yyyy HH:mm",
      { locale: fr },
    );
  } catch {
    return "-";
  }
};

// NOUVEAU - Formatage de l'heure uniquement
export const formatTime = (date) => {
  if (!date) return "-";
  try {
    return format(typeof date === "string" ? parseISO(date) : date, "HH:mm", {
      locale: fr,
    });
  } catch {
    return "-";
  }
};

// NOUVEAU - Formatage pour les inputs date (YYYY-MM-DD)
export const formatDateForInput = (date) => {
  if (!date) return "";
  try {
    return format(
      typeof date === "string" ? parseISO(date) : date,
      "yyyy-MM-dd",
      { locale: fr },
    );
  } catch {
    return "";
  }
};

// NOUVEAU - Formatage pour les inputs datetime-local
export const formatDateTimeForInput = (date) => {
  if (!date) return "";
  try {
    return format(
      typeof date === "string" ? parseISO(date) : date,
      "yyyy-MM-dd'T'HH:mm",
      { locale: fr },
    );
  } catch {
    return "";
  }
};

const currencyFormatter = new Intl.NumberFormat("fr-FR", {
  style: "currency",
  currency: "EUR",
});

export const formatCurrency = (amount) => currencyFormatter.format(amount || 0);

export const getInitials = (nom, prenom) => {
  if (!nom && !prenom) return "?";
  return `${nom?.charAt(0) || ""}${prenom?.charAt(0) || ""}`.toUpperCase();
};

export const truncateText = (text, maxLength = 100) => {
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + "...";
};

export const capitalize = (str) => {
  if (!str) return "";
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
};

export const generateColor = (str) => {
  if (!str) return "#3b82f6";
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }
  const hue = hash % 360;
  return `hsl(${hue}, 70%, 50%)`;
};

// NOUVEAU - Vérifier si une date est aujourd'hui
export const isToday = (date) => {
  if (!date) return false;
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    const today = new Date();
    return (
      d.getDate() === today.getDate() &&
      d.getMonth() === today.getMonth() &&
      d.getFullYear() === today.getFullYear()
    );
  } catch {
    return false;
  }
};

// NOUVEAU - Vérifier si une date est passée
export const isPast = (date) => {
  if (!date) return false;
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return d < new Date();
  } catch {
    return false;
  }
};

// NOUVEAU - Vérifier si une date est future
export const isFuture = (date) => {
  if (!date) return false;
  try {
    const d = typeof date === "string" ? parseISO(date) : date;
    return d > new Date();
  } catch {
    return false;
  }
};

// Nombre de jours calendaires (pas d'heures) avant `date` — troncature à
// minuit pour que "aujourd'hui"/"demain" ne dépendent pas de l'heure
// actuelle (même logique que côté backend, voir meteoClient.js). Utilisé
// par le compte à rebours de ProchaineSortieCard.
export const joursRestants = (date) => {
  const aujourdhui = new Date();
  aujourdhui.setHours(0, 0, 0, 0);
  const cible = new Date(date);
  cible.setHours(0, 0, 0, 0);
  return Math.round((cible - aujourdhui) / 86400000);
};

// Texte convivial du compte à rebours à partir de joursRestants — pour une
// date déjà passée (jours < 0), appelant a la responsabilité de ne pas
// l'afficher (le sens d'un "compte à rebours" n'existe que pour l'avenir).
export const formatCompteARebours = (jours) =>
  jours <= 0 ? "Aujourd'hui" : jours === 1 ? "Demain" : `Dans ${jours} jours`;

// NOUVEAU - Formater une durée en minutes/secondes
export const formatDuration = (minutes) => {
  if (!minutes && minutes !== 0) return "-";
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours > 0) {
    return `${hours}h${mins > 0 ? ` ${mins}min` : ""}`;
  }
  return `${mins}min`;
};

// NOUVEAU - Formater un numéro de téléphone
export const formatPhoneNumber = (phone) => {
  if (!phone) return "-";
  const cleaned = phone.replace(/\s/g, "");
  if (cleaned.length === 10) {
    return cleaned.replace(/(.{2})/g, "$1 ").trim();
  }
  return phone;
};
